import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Fetches "Complete the set" complementary product recommendations for the
 * most recently added cart line and injects them into the cart drawer's
 * `#cart-drawer-recommendations` container.
 *
 * Recommendations are requested via the dedicated recommendations endpoint
 * (`routes.product_recommendations_url`), which renders `sections/cart-drawer-recommendations.liquid`
 * and returns raw HTML (not the JSON Section Rendering API shape).
 *
 * Re-runs on every cart update rather than caching by product id: the cart
 * drawer's own section re-render (triggered by the same cart update) replaces
 * `#cart-drawer-recommendations` with a fresh, empty copy from the server, so
 * this container must always be repopulated afterward — skipping a refetch
 * because the top product id hasn't changed would leave it empty.
 */

const CONTAINER_SELECTOR = '#cart-drawer-recommendations';
const SECTION_ID = 'cart-drawer-recommendations';
const CART_DRAWER_ID = 'cart-drawer';

/** @type {AbortController | null} */
let activeRequest = null;

async function updateCartDrawerRecommendations() {
  const container = document.querySelector(CONTAINER_SELECTOR);
  if (!container) return;

  /** @type {{items: Array<{product_id: number}>}} */
  let cart;
  try {
    const response = await fetch(`${Theme.routes.cart_url}.json`);
    cart = await response.json();
  } catch (error) {
    return;
  }

  if (!cart.items || cart.items.length === 0) {
    container.classList.remove('is-loading');
    container.innerHTML = '';
    return;
  }

  // Shopify's cart.json lists items newest-first.
  const productId = cart.items[0].product_id;

  activeRequest?.abort();
  activeRequest = new AbortController();
  const { signal } = activeRequest;

  // Keep whatever is currently showing (this container is exempt from the cart
  // drawer's own section morph via `data-skip-subtree-update`) and just dim it
  // with a loading indicator while the new recommendations load, instead of
  // clearing it up front — avoids the empty-then-repopulate flash.
  container.classList.add('is-loading');

  const buildUrl = (intent) =>
    `${Theme.routes.product_recommendations_url}?section_id=${SECTION_ID}&product_id=${productId}&intent=${intent}&limit=3`;

  const fetchRecommendations = async (intent) => {
    const response = await fetch(buildUrl(intent), { signal });
    const text = await response.text();
    return new DOMParser().parseFromString(text, 'text/html').querySelector('.cart-drawer-recommendations');
  };

  try {
    // Complementary recommendations are curated from order history and may be
    // empty on stores without enough sales data yet — fall back to related.
    let recommendations = await fetchRecommendations('complementary');
    if (!recommendations) recommendations = await fetchRecommendations('related');

    container.innerHTML = '';
    if (recommendations) container.appendChild(recommendations);
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.warn('[cart-drawer-recommendations] Failed to load recommendations:', error);
    }
  } finally {
    if (!signal.aborted) container.classList.remove('is-loading');
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const { promise } = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);
  if (promise) {
    promise.then(() => updateCartDrawerRecommendations()).catch(() => {});
  } else {
    updateCartDrawerRecommendations();
  }
});

document.addEventListener(DrawerOpenEvent.eventName, (event) => {
  if (event.target instanceof Element && event.target.id === CART_DRAWER_ID) {
    updateCartDrawerRecommendations();
  }
});
